/**
 * ToastContext — global toast notification system for Reve Workspace.
 *
 * Usage:
 *   const { showToast } = useToast();
 *   showToast('Post published successfully', 'success');
 *   showToast('Failed to save changes', 'error');
 *   showToast('Your session will expire soon', 'warning');
 *   showToast('New message received', 'info');
 *
 * Optional action button (RW-1332 — "forgiveness over prevention" undo affordance):
 *   showToast('12 contacts archived', 'success', 8000, {
 *     action: { label: 'Undo', onClick: () => restore(ids) },
 *   });
 * Clicking the action runs onClick and dismisses the toast.
 *
 * `showToast` returns the toast's id; `dismissToast(id)` takes it down early
 * (RW-2440: an Undo toast goes when the thing it would undo is over).
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useFabClearance } from '../hooks/useFabClearance';
import { toastBottom } from '../lib/fabStack';

const ToastContext = createContext(null);

let _nextId = 0;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef({});

  const removeToast = useCallback((id) => {
    // Start exit animation
    setToasts((prev) => prev.map((t) => (t.id === id ? { ...t, exiting: true } : t)));
    // Remove after animation completes
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
      if (timersRef.current[id]) {
        clearTimeout(timersRef.current[id]);
        delete timersRef.current[id];
      }
    }, 300);
  }, []);

  const showToast = useCallback((message, type = 'info', duration = 4000, options = {}) => {
    const id = ++_nextId;
    const action = options && options.action && typeof options.action.onClick === 'function' && options.action.label
      ? { label: String(options.action.label), onClick: options.action.onClick }
      : null;
    setToasts((prev) => [{ id, message, type, action, exiting: false }, ...prev].slice(0, 6));
    if (duration > 0) {
      timersRef.current[id] = setTimeout(() => removeToast(id), duration);
    }
    return id;
  }, [removeToast]);

  // Both callbacks are stable, so the value is too. An inline object here was a
  // new value on every toast add/exit/remove, re-rendering every useToast()
  // consumer in the app three times per toast.
  const value = useMemo(() => ({ showToast, dismissToast: removeToast }), [showToast, removeToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={removeToast} />
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within a ToastProvider');
  return ctx;
}

/** useToast, or null outside a ToastProvider (components that also render bare in tests). */
export function useOptionalToast() {
  return useContext(ToastContext);
}

/* ─── Toast type config (Reve brand colors) ─── */
const TYPE_CONFIG = {
  success: { bg: '#ECFDF5', border: '#10B981', color: '#065F46', icon: '✓' },
  error:   { bg: '#FEF2F2', border: '#EE8172', color: '#991B1B', icon: '✕' },
  warning: { bg: '#FFFBEB', border: '#F59E0B', color: '#92400E', icon: '⚠' },
  info:    { bg: '#EEF2FF', border: '#5C50A1', color: '#3730A3', icon: 'ℹ' },
};

/* ─── Inline keyframes (injected once) ─── */
const STYLE_ID = 'reve-toast-styles';
if (typeof document !== 'undefined' && !document.getElementById(STYLE_ID)) {
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    @keyframes reve-toast-in { from { transform: translateX(120%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
    @keyframes reve-toast-out { from { transform: translateX(0); opacity: 1; } to { transform: translateX(120%); opacity: 0; } }
  `;
  document.head.appendChild(style);
}

/**
 * Is the viewport narrow enough for the bottom-right button column to be in the
 * toast's way? Deliberately `innerWidth` rather than the shared `useIsMobile`,
 * which calls `window.matchMedia` — ToastProvider wraps most of the rendered
 * test suite, and matchMedia is not in jsdom, so importing that hook here turns
 * every one of those suites red. innerWidth is always a number in jsdom (1024),
 * so tests get the desktop offset and the behaviour they had before.
 */
function useToastNarrow() {
  const read = () => (typeof window !== 'undefined' && typeof window.innerWidth === 'number'
    ? window.innerWidth <= 900
    : false);
  const [narrow, setNarrow] = useState(read);
  useEffect(() => {
    const onResize = () => setNarrow(read());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return narrow;
}

function ToastContainer({ toasts, onDismiss }) {
  // RW-2271 (F11): the toast stack sits above the bottom-right button column
  // instead of on top of it. Found by the RW-2269 walk — at 390×844 the old
  // hard-coded `bottom: 24` landed the stack on the create FAB and the mobile
  // bottom nav, and each toast carries `pointerEvents: 'auto'`, so while a
  // toast was up neither could be tapped. Reuses RW-1486's clearance channel,
  // exactly as the three FABs do (fabStack.js), rather than a fourth set of
  // hand-written offsets. Hooks run before the early return — a container that
  // sometimes calls them and sometimes doesn't is a hook-order crash.
  const lift = useFabClearance();
  const mobile = useToastNarrow();
  const bottom = toastBottom(lift, mobile);
  if (toasts.length === 0) return null;
  return (
    <div
      aria-live="polite"
      style={{
        position: 'fixed', bottom, right: 24, zIndex: 10000,
        display: 'flex', flexDirection: 'column', gap: 8,
        pointerEvents: 'none', maxWidth: 380,
      }}
    >
      {toasts.map((t) => {
        const cfg = TYPE_CONFIG[t.type] || TYPE_CONFIG.info;
        return (
          <div
            key={t.id}
            role="alert"
            data-toast=""
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '12px 16px',
              background: cfg.bg,
              border: `1px solid ${cfg.border}`,
              borderLeft: `4px solid ${cfg.border}`,
              borderRadius: 8,
              boxShadow: '0 4px 16px rgba(15,16,17,0.12)',
              fontFamily: "'Jost', sans-serif",
              fontSize: 14, fontWeight: 500, color: cfg.color,
              pointerEvents: 'auto',
              animation: t.exiting ? 'reve-toast-out 0.3s ease forwards' : 'reve-toast-in 0.3s ease forwards',
            }}
          >
            <span style={{ fontSize: 16, fontWeight: 700, lineHeight: 1, flexShrink: 0 }}>
              {cfg.icon}
            </span>
            <span style={{ flex: 1, lineHeight: 1.4 }}>{t.message}</span>
            {t.action && (
              <button
                onClick={() => { t.action.onClick(); onDismiss(t.id); }}
                style={{
                  background: 'transparent', border: `1.5px solid ${cfg.border}`,
                  borderRadius: 7, padding: '5px 12px', cursor: 'pointer',
                  color: cfg.color, fontFamily: "'Jost', sans-serif",
                  fontSize: 13, fontWeight: 700, lineHeight: 1, flexShrink: 0,
                  whiteSpace: 'nowrap',
                }}
              >
                {t.action.label}
              </button>
            )}
            <button
              onClick={() => onDismiss(t.id)}
              aria-label="Dismiss"
              style={{
                background: 'none', border: 'none', padding: 4, cursor: 'pointer',
                color: cfg.color, fontSize: 16, fontWeight: 700, lineHeight: 1, flexShrink: 0,
                opacity: 0.6,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
              onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.6'; }}
            >
              ✕
            </button>
          </div>
        );
      })}
    </div>
  );
}
